/**
 * Parses and groups raw API scope strings of the form `domain/resource:action`
 * (e.g. `auth/users:read`). Verbatim strings from `/auth/me`; no friendly-label
 * translation. Split is on the FIRST colon so extra segments (`auth/clients:read:private`)
 * stay in the verb and never throw.
 */
export interface ParsedScope {
  /** Original scope string, untrimmed. */
  raw: string;
  /** First path segment of the resource, e.g. `auth`. */
  domain: string;
  /** Full path before the first colon, e.g. `auth/users`. */
  resource: string;
  /** Everything after the first colon, e.g. `read` or `read:private`; `''` when there is no colon. */
  verb: string;
}

export interface ScopeGroup {
  domain: string;
  items: ParsedScope[];
  count: number;
}

export function parseScope(raw: string): ParsedScope {
  const trimmed = raw.trim();
  const colonIndex = trimmed.indexOf(':');
  const resource = colonIndex === -1 ? trimmed : trimmed.slice(0, colonIndex);
  const verb = colonIndex === -1 ? '' : trimmed.slice(colonIndex + 1);
  const slashIndex = resource.indexOf('/');
  const domain = slashIndex === -1 ? resource : resource.slice(0, slashIndex);
  return { raw, domain, resource, verb };
}

export function groupScopes(scopes: string[]): ScopeGroup[] {
  const byDomain = new Map<string, ParsedScope[]>();
  for (const raw of scopes) {
    if (!raw || !raw.trim()) {
      continue;
    }
    const parsed = parseScope(raw);
    // Group by full resource path (e.g. "auth/users") for finer-grained grouping
    const key = parsed.resource;
    const bucket = byDomain.get(key);
    if (bucket) {
      bucket.push(parsed);
    } else {
      byDomain.set(key, [parsed]);
    }
  }
  return [...byDomain.entries()]
    .map(([domain, items]) => ({
      domain,
      items: items.sort((a, b) => a.resource.localeCompare(b.resource) || a.verb.localeCompare(b.verb)),
      count: items.length,
    }))
    .sort((a, b) => a.domain.localeCompare(b.domain));
}
